import { useEffect } from 'react';

import { Box } from '@mui/material';
import { ActionCreatorWithOptionalPayload, ActionCreatorWithoutPayload } from '@reduxjs/toolkit';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router';

import CurationNameInputWithState from 'domains/campaigns/components/CurationNameInputWithState';
import { MetaDataNode } from 'domains/campaigns/state/createCampaignSlice';
import { selectContentFieldsByNodeId, selectMetadataRoute } from 'domains/campaigns/state/selectors';
import { CampaignKey } from 'domains/campaigns/types';
import EmailPreviewContent from 'domains/content/components/EmailPreviewContent';
import { isTemplate } from 'domains/content/types';
import Flow from 'domains/core/components/Flow/Flow';
import ErrorSnackbar from 'domains/core/components/Snackbars/ErrorSnackbar';
import SuccessDialog from 'domains/core/components/SuccessDialog';
import { recordSatismeterEvent } from 'helpers/injectSatismeter/injectSatismeter';
import useSegments from 'hooks/queries/useSegments';
import useSenderProfile from 'hooks/queries/useSenderProfile';
import useTemplate from 'hooks/queries/useTemplate';
import useTemplates from 'hooks/queries/useTemplates';
import { URLPaths } from 'models/enums';
import { RootState } from 'state';
import { onLeaveCurationWorkflow } from 'state/appVisual/appVisualActions';
import { route as routeSelector } from 'state/selectors';

export type Props = {
    ButtonContinue: JSX.Element;
    CampaignFlowRouteLeavingGuard: JSX.Element;
    NavBar: JSX.Element;
    Navigation: JSX.Element;
    actionCreatorEditName: ActionCreatorWithOptionalPayload<string, string>;
    actionCreatorResetState: ActionCreatorWithoutPayload<string>;
    actionCreatorSetContentPreviewNodeId: ActionCreatorWithOptionalPayload<string, string>;
    campaignKey: CampaignKey;
    createCampaignError: Error;
    isCreateCampaignError: boolean;
    isCreateCampaignLoading: boolean;
    isSuccessDialogOpen: boolean;
    mainRoute: string;
    selectIsCampaignRouteDisabled: (state: RootState) => (route: URLPaths) => boolean;
    selectMetaDataNode: (state: RootState) => MetaDataNode;
    setIsSuccessDialogOpen: (isOpen: boolean) => void;
};

const CampaignFlow = ({
    ButtonContinue,
    CampaignFlowRouteLeavingGuard,
    NavBar,
    Navigation,
    actionCreatorEditName,
    actionCreatorResetState,
    actionCreatorSetContentPreviewNodeId,
    campaignKey,
    createCampaignError,
    isCreateCampaignError,
    isCreateCampaignLoading,
    isSuccessDialogOpen,
    mainRoute,
    selectIsCampaignRouteDisabled,
    selectMetaDataNode,
    setIsSuccessDialogOpen,
}: Props) => {
    const dispatch = useDispatch();
    const history = useHistory();

    const route = useSelector(routeSelector);
    const isCampaignRouteDisabled = useSelector(selectIsCampaignRouteDisabled);
    const metadataNode = useSelector(selectMetaDataNode);
    const contentFields = useSelector(selectContentFieldsByNodeId(campaignKey, metadataNode.contentPreviewNodeId));

    const {
        senderProfileId: previewSenderProfileId,
        templateId: previewTemplateId,
        preHeader: previewPreheader,
        subjectLine: previewSubjectLine,
    } = contentFields;

    const { data: previewSenderProfile } = useSenderProfile(previewSenderProfileId, {
        enabled: Boolean(previewSenderProfileId),
    });

    const { data: previewTemplate } = useTemplate({
        templateId: `${previewTemplateId}`,
        reactQueryOptions: {
            enabled: Boolean(metadataNode.contentPreviewNodeId),
        },
    });

    useSegments(); // Pre-cache for upcoming form (CurationAudienceForm)
    useTemplates(); // Pre-cache for upcoming form (CurationContentForm)

    const metadataRoute = useSelector(selectMetadataRoute(campaignKey));

    useEffect(() => {
        // If the user manually routes to a page that is disabled (i.e refreshes, or changes url), then redirect to the main curation page
        if (isCampaignRouteDisabled(route)) {
            history.push(metadataRoute);
            dispatch(actionCreatorResetState());
        }

        // Close the preview so that on the next content form, the preview does not automatically open when a template is selected.
        if (route.includes(mainRoute)) {
            dispatch(actionCreatorSetContentPreviewNodeId(null));
        }

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [route]);

    const handleExitCurationWorkflow = (path: string) => {
        history.push(path);
        dispatch(onLeaveCurationWorkflow());
        dispatch(actionCreatorResetState());
        recordSatismeterEvent('publish_curation_popup');
    };

    return (
        <Flow
            feedback={
                <>
                    {CampaignFlowRouteLeavingGuard}
                    {isCreateCampaignError && !isCreateCampaignLoading && (
                        <ErrorSnackbar errorMessage={createCampaignError.message} />
                    )}
                    <SuccessDialog
                        title="high five!"
                        body="Nice work, you've successfully scheduled your Curation. Once it sends, visit Insights to monitor its performance."
                        isOpen={isSuccessDialogOpen}
                        setIsOpen={setIsSuccessDialogOpen}
                        onClose={() => setIsSuccessDialogOpen(false)}
                        buttonOneText="Insights"
                        buttonTwoText="Curations"
                        buttonOneOnClick={() => handleExitCurationWorkflow(URLPaths.INSIGHTS)}
                        buttonTwoOnClick={() => handleExitCurationWorkflow(URLPaths.CURATIONS)}
                    />
                </>
            }
            topLeft={
                <CurationNameInputWithState
                    editNameActionCreator={actionCreatorEditName}
                    curationName={metadataNode.name}
                    isHidden={route === metadataRoute}
                />
            }
            topRight={ButtonContinue}
            bottomLeft={Navigation}
            bottomRight={
                <>
                    <EmailPreviewContent
                        emailTemplateHTML={isTemplate(previewTemplate) && previewTemplate.html}
                        fromAddress={previewSenderProfile?.from_address}
                        isGridItem
                        isOpen={Boolean(metadataNode.contentPreviewNodeId)}
                        preHeader={previewPreheader}
                        subjectLine={previewSubjectLine}
                        onClose={() => dispatch(actionCreatorSetContentPreviewNodeId(null))}
                    />
                    <Box
                        data-testid={Boolean(metadataNode.contentPreviewNodeId) ? 'preview' : ''}
                        display={Boolean(metadataNode.contentPreviewNodeId) ? 'none' : 'block'}
                        height="100%"
                    >
                        {NavBar}
                    </Box>
                </>
            }
        />
    );
};

export default CampaignFlow;
